// @ts-ignore
import { VALID_SITEWIDE_PACK_TYPES } from '@/services/static/packs/build-a-pack';
import { formatMonetaryValue } from '@/utils/format';
import { getPackPrice } from '@/services/static/packs/pack-price';

import {
	BUILD_A_PACK,
	CAN_CHANGE_QUANTITY,
	DISCOUNTED_LINE_PRICE,
	FROM_PACK_PRODUCT,
	FROM_PACK_PRODUCT_TITLE,
	FROM_PACK_SKU,
	FROM_PRODUCT_HANDLE,
	HAS_SUB_ITEMS,
	IS_HIDDEN,
	LINE_PRICE,
	MIXED_PACK_BUILDER,
	ORIGINAL_PRICE,
	PACK_CHILD,
	PACK_CONFIG_ID,
	PACK_IS_SPECIAL,
	PACK_MASTER,
	PRODUCT_TYPE,
	SHOW_DISCOUNT,
	SITEWIDE_PACK_BUILDER,
	SUB_ITEM_PREFIX,
} from '@/services/static/attribute-keys';

import {
	BuilderConfig,
	CartAnalyticsInfo,
	CartData,
	CartLine,
	PackConfig,
	ParentProduct,
} from '@/services/static/packs/types';

const getPackCartData = (
	builderConfig: BuilderConfig,
	packConfig: PackConfig,
	parentProduct: ParentProduct,
	isSitewidePack: boolean,
) => {
	// there's something very wrong
	if (
		!builderConfig ||
		!packConfig ||
		!packConfig.selectedProducts ||
		packConfig.selectedProducts.length === 0
	) {
		return;
	}

	// the pack doesn't meet the minimum products for the builder
	const productCountMin = builderConfig.productCountMin || 1;
	if (packConfig.selectedProducts.length < productCountMin) {
		return;
	}

	// there are too many products
	if (
		builderConfig.productCountMax &&
		builderConfig.productCountMax < packConfig.selectedProducts.length
	) {
		return;
	}

	// these attributes get assigned to every line item
	const baseAttributes = [
		{ key: BUILD_A_PACK, value: 'true' },
		{ key: FROM_PACK_PRODUCT, value: parentProduct?.id || 'unknown' },
		{ key: FROM_PACK_PRODUCT_TITLE, value: parentProduct?.title || 'unknown' },
		{ key: FROM_PACK_SKU, value: parentProduct.variants?.[0]?.sku || 'unknown' },
		{ key: PACK_CONFIG_ID, value: builderConfig.id || 'unknown' },
	];

	// i.e. boxer x thong pack
	if (builderConfig.isMixedPackBuilder) {
		baseAttributes.push({
			key: MIXED_PACK_BUILDER,
			value: 'true',
		});
	}

	// i.e. christmas box + underwear + cream
	if (builderConfig.isSpecialPack) {
		baseAttributes.push({
			key: PACK_IS_SPECIAL,
			value: 'true',
		});
	}

	const prices = getPackPrice({
		builderConfig: builderConfig,
		builderId: builderConfig.id,
		builderType: builderConfig.builderType,
		selectedBox: packConfig.box,
		selectedProducts: packConfig.selectedProducts,
		selectedExtras: packConfig.selectedExtras,
	});

	// lines from the cart
	const cartData = packConfig.selectedProducts.reduce(
		(memo, selectedProduct) => {
			if (isSitewidePack && VALID_SITEWIDE_PACK_TYPES.includes(selectedProduct.productType)) {
				baseAttributes.push({ key: SITEWIDE_PACK_BUILDER, value: 'true' });
				baseAttributes.push({ key: PRODUCT_TYPE, value: selectedProduct.productType || 'unknown' });
			}

			const cartLine: CartLine = {
				merchandiseId: selectedProduct.shopifyVariantId,
				quantity: 1,
				attributes: [...baseAttributes],
			};

			if (builderConfig.isSpecialPack) {
				cartLine.attributes.push(
					{ key: PACK_CHILD, value: 'true' },
					{ key: IS_HIDDEN, value: 'true' },
					{ key: CAN_CHANGE_QUANTITY, value: 'false' },
				);

				// not sure what this is for -- we think maybe something for thomas
				// we add all the items as attributes to the box
				if (memo.subItemAttributes[selectedProduct.shopifyVariantId]) {
					memo.subItemAttributes[selectedProduct.shopifyVariantId].quantity++;
				} else {
					memo.subItemAttributes[selectedProduct.shopifyVariantId] = {
						title: selectedProduct?.title?.split('|')?.[0]?.trim() || 'unknown',
						quantity: 1,
					};
				}
			}

			// intentionally skipping product analytics here for add to cart event
			memo.lines.push(cartLine);
			return memo;
		},
		{ analytics: [], lines: [], subItemAttributes: {} } as CartData,
	);

	// there are extra items involved, add those
	if (packConfig.selectedExtras && packConfig.selectedExtras.length > 0) {
		const extraLines = packConfig.selectedExtras.reduce(
			(memo, extraItem) => {
				if (!extraItem?.variantId) {
					return memo;
				}

				const cartLine: CartLine = {
					merchandiseId: extraItem.variantId,
					quantity: 1,
					attributes: [...baseAttributes],
				};

				memo.lines.push(cartLine);

				if (builderConfig.isSpecialPack) {
					cartLine.attributes.push(
						{ key: PACK_CHILD, value: 'true' },
						{ key: IS_HIDDEN, value: 'true' },
						{ key: CAN_CHANGE_QUANTITY, value: 'false' },
					);

					if (cartData.subItemAttributes[extraItem.variantId]) {
						cartData.subItemAttributes[extraItem.variantId].quantity++;
					} else {
						cartData.subItemAttributes[extraItem.variantId] = {
							quantity: 1,
							title: extraItem?.product?.title?.split('|')?.[0].trim() || 'unknown',
						};
					}
				}

				memo.analytics.push({
					price: extraItem.product?.prices?.[0] || '',
					product: extraItem.product,
					quantity: 1,
					variantId: extraItem.variantId || '',
					variantSku: extraItem.variantSku || '',
					variantTitle: extraItem.variantTitle || '',
				});

				return memo;
			},
			{ analytics: [], lines: [], subItemAttributes: {} } as CartData,
		);

		cartData.lines.push(...extraLines.lines);
	}

	// there's a box involved, add that
	if (packConfig.box?.variants?.[0]?.id) {
		const boxLine = {
			merchandiseId: packConfig.box.variants?.[0]?.id,
			quantity: 1,
			attributes: [...baseAttributes],
		};

		if (builderConfig.isSpecialPack) {
			boxLine.attributes.push(
				{ key: PACK_MASTER, value: 'true' },
				{ key: HAS_SUB_ITEMS, value: 'true' },
				{ key: FROM_PRODUCT_HANDLE, value: `/products/${parentProduct.handle}` },
				{ key: SHOW_DISCOUNT, value: 'true' },
				{ key: CAN_CHANGE_QUANTITY, value: 'false' },
				{ key: ORIGINAL_PRICE, value: formatMonetaryValue(prices.priceRetailTotal).toString() },
				{ key: LINE_PRICE, value: formatMonetaryValue(prices.priceDiscountTotal).toString() },
				{
					key: DISCOUNTED_LINE_PRICE,
					value: formatMonetaryValue(prices.priceDiscountTotal).toString(),
				},
			);

			Object.values(cartData.subItemAttributes).forEach((value, index) => {
				boxLine.attributes.push({
					key: `${SUB_ITEM_PREFIX}${index}`,
					value: `${value?.title} X ${value.quantity}`,
				});
			});
		}

		const boxItemAnalytics: CartAnalyticsInfo = {
			price: packConfig.box.prices?.[0] || '',
			product: packConfig.box,
			quantity: 1,
			variantId: packConfig.box.variants?.[0]?.id || '',
			variantSku: packConfig.box.variants?.[0]?.sku || '',
			variantTitle: packConfig.box?.variants?.[0]?.title || '',
		};

		cartData.analytics.push(boxItemAnalytics);
		cartData.lines.push(boxLine);
	}

	const analyticsProduct = { ...parentProduct };
	analyticsProduct.prices = [
		formatMonetaryValue(prices.priceDiscountTotal),
		formatMonetaryValue(prices.priceDiscountTotal),
	];

	return {
		analytics: cartData.analytics,
		analyticsProduct,
		lines: cartData.lines,
	};
};

module.exports = {
	getPackCartData,
};
